import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { sv } from 'date-fns/locale';
import { format } from 'date-fns';
import { BRAND_COLORS, alpha } from '../../styles/theme';
import { FileFilters, FileSortField, FileSortOrder } from '../Dashboard/types/dashboard.types';
import { useTranslation } from '../../i18n';

interface FileFiltersDialogProps {
  open: boolean;
  onClose: () => void;
  fileFilters: FileFilters;
  serialNumbers: string[];
  onFilterChange: (filters: Partial<FileFilters>) => void;
  onResetFilters: () => void;
}

const FileFiltersDialog: React.FC<FileFiltersDialogProps> = ({
  open,
  onClose,
  fileFilters,
  serialNumbers,
  onFilterChange,
  onResetFilters,
}) => {
  const { t } = useTranslation();

  const selectStyles = {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(BRAND_COLORS.forestGreen.main, 0.2),
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(BRAND_COLORS.forestGreen.main, 0.3),
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: BRAND_COLORS.forestGreen.main,
    },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: BRAND_COLORS.forestGreen.main }}>
        {t('filterDialog.fileFilters')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Drone Serial Filter */}
          <FormControl fullWidth size="medium" sx={{ mb: 2 }}>
            <InputLabel>{t('filterDialog.droneSerial')}</InputLabel>
            <Select
              value={fileFilters.selectedSerial || 'All'}
              label={t('filterDialog.droneSerial')}
              onChange={(e) =>
                onFilterChange({
                  selectedSerial: e.target.value,
                })
              }
              sx={selectStyles}
            >
              <MenuItem key="All" value="All">
                {t('common.all')}
              </MenuItem>
              {serialNumbers.map((serial) => (
                <MenuItem key={serial} value={serial}>
                  {serial}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date Range Filters */}
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={sv}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <DatePicker
                label={t('filterDialog.startDate')}
                value={fileFilters.startDate ? new Date(fileFilters.startDate) : null}
                onChange={(date: Date | null) => {
                  onFilterChange({
                    startDate: date ? format(date, 'yyyy-MM-dd') : null,
                  });
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'medium',
                    sx: selectStyles,
                  },
                }}
              />
              <DatePicker
                label={t('filterDialog.endDate')}
                value={fileFilters.endDate ? new Date(fileFilters.endDate) : null}
                onChange={(date: Date | null) => {
                  onFilterChange({
                    endDate: date ? format(date, 'yyyy-MM-dd') : null,
                  });
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'medium',
                    sx: selectStyles,
                  },
                }}
              />
            </Box>
          </LocalizationProvider>

          {/* Sort Options */}
          <Typography
            variant="subtitle2"
            sx={{ mb: 1, mt: 1, color: BRAND_COLORS.forestGreen.main }}
          >
            {t('filterDialog.sortOptions')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
            <FormControl sx={{ flex: 1 }} size="medium">
              <InputLabel>{t('filterDialog.sortBy')}</InputLabel>
              <Select
                value={fileFilters.sortBy || 'name'}
                label={t('filterDialog.sortBy')}
                onChange={(e) => {
                  onFilterChange({
                    sortBy: e.target.value as FileSortField,
                  });
                }}
                sx={selectStyles}
              >
                <MenuItem value="name">{t('filterDialog.sortByName')}</MenuItem>
                <MenuItem value="date">{t('filterDialog.sortByDate')}</MenuItem>
                <MenuItem value="serial">{t('filterDialog.sortBySerial')}</MenuItem>
              </Select>
            </FormControl>
            <ToggleButtonGroup
              value={fileFilters.sortOrder || 'asc'}
              exclusive
              onChange={(_e, newOrder: FileSortOrder | null) => {
                if (newOrder !== null) {
                  onFilterChange({ sortOrder: newOrder });
                }
              }}
              aria-label="sort direction"
              sx={{
                '& .MuiToggleButton-root': {
                  borderColor: alpha(BRAND_COLORS.forestGreen.main, 0.2),
                  '&:hover': {
                    borderColor: alpha(BRAND_COLORS.forestGreen.main, 0.3),
                    backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.04),
                  },
                  '&.Mui-selected': {
                    borderColor: BRAND_COLORS.forestGreen.main,
                    backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.08),
                    color: BRAND_COLORS.forestGreen.main,
                    '&:hover': {
                      backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.12),
                    },
                  },
                },
              }}
            >
              <ToggleButton value="asc" aria-label="ascending">
                <ArrowUpwardIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t('filterDialog.ascending')}
              </ToggleButton>
              <ToggleButton value="desc" aria-label="descending">
                <ArrowDownwardIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t('filterDialog.descending')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Include untagged data toggle */}
          {(fileFilters.selectedSerial !== 'All' ||
            fileFilters.startDate ||
            fileFilters.endDate) && (
            <FormControlLabel
              control={
                <Switch
                  checked={fileFilters.includeUntagged}
                  onChange={(e) => onFilterChange({ includeUntagged: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: BRAND_COLORS.forestGreen.main,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: BRAND_COLORS.forestGreen.main,
                    },
                  }}
                />
              }
              label={t('fileManagement.includeUntaggedData')}
              sx={{ mb: 2 }}
            />
          )}

          {/* Reset File Filters Button */}
          <Button
            variant="outlined"
            onClick={onResetFilters}
            sx={{
              color: BRAND_COLORS.forestGreen.main,
              borderColor: alpha(BRAND_COLORS.forestGreen.main, 0.3),
              '&:hover': {
                borderColor: BRAND_COLORS.forestGreen.main,
                backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.04),
              },
            }}
          >
            {t('filterDialog.resetFileFilters')}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileFiltersDialog;
